import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { LayoutTransition } from "./components/LayoutTransition";
import { PageLoader } from "./components/PageLoader";
import { ScrollToTop } from "./components/ScrollToTop";
import { useState, useEffect } from "react";

import Index from "./pages/Index";
import Services from "./pages/Services";
import Tracking from "./pages/Tracking";
import About from "./pages/About";
import Contact from "./pages/Contact";
import OceanFreight from "./pages/OceanFreight";
import AirFreight from "./pages/AirFreight";
import Intermodal from "./pages/Intermodal";
import ProjectCargo from "./pages/ProjectCargo";
import CustomsBrokerage from "./pages/CustomsBrokerage";
import Insurance from "./pages/Insurance";
import SupplyChain from "./pages/SupplyChain";
import Warehousing from "./pages/Warehousing";
import FAQ from "./pages/FAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import CookiePolicy from "./pages/CookiePolicy";
import CancellationPolicy from "./pages/CancellationPolicy";
import Reviews from "./pages/Reviews";
import Sustainability from "./pages/Sustainability";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AddShipment from "./pages/admin/AddShipment";
import EditShipment from "./pages/admin/EditShipment";
import StatusUpdates from "./pages/admin/StatusUpdates";

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
        {loading && <PageLoader key="loader" />}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<LayoutTransition><Index /></LayoutTransition>} />
          <Route path="/services" element={<LayoutTransition><Services /></LayoutTransition>} />
          <Route path="/ocean-freight" element={<LayoutTransition><OceanFreight /></LayoutTransition>} />
          <Route path="/air-freight" element={<LayoutTransition><AirFreight /></LayoutTransition>} />
          <Route path="/intermodal" element={<LayoutTransition><Intermodal /></LayoutTransition>} />
          <Route path="/project-cargo" element={<LayoutTransition><ProjectCargo /></LayoutTransition>} />
          <Route path="/customs-brokerage" element={<LayoutTransition><CustomsBrokerage /></LayoutTransition>} />
          <Route path="/insurance" element={<LayoutTransition><Insurance /></LayoutTransition>} />
          <Route path="/supply-chain" element={<LayoutTransition><SupplyChain /></LayoutTransition>} />
          <Route path="/warehousing" element={<LayoutTransition><Warehousing /></LayoutTransition>} />
          <Route path="/tracking" element={<LayoutTransition><Tracking /></LayoutTransition>} />
          <Route path="/about" element={<LayoutTransition><About /></LayoutTransition>} />
          <Route path="/contact" element={<LayoutTransition><Contact /></LayoutTransition>} />
          <Route path="/faq" element={<LayoutTransition><FAQ /></LayoutTransition>} />
          <Route path="/reviews" element={<LayoutTransition><Reviews /></LayoutTransition>} />
          <Route path="/sustainability" element={<LayoutTransition><Sustainability /></LayoutTransition>} />
          <Route path="/privacy-policy" element={<LayoutTransition><PrivacyPolicy /></LayoutTransition>} />
          <Route path="/terms-conditions" element={<LayoutTransition><TermsConditions /></LayoutTransition>} />
          <Route path="/cookie-policy" element={<LayoutTransition><CookiePolicy /></LayoutTransition>} />
          <Route path="/cancellation-policy" element={<LayoutTransition><CancellationPolicy /></LayoutTransition>} />
          <Route path="/admin" element={<LayoutTransition><AdminLogin /></LayoutTransition>} />
          <Route path="/admin/dashboard" element={<LayoutTransition><AdminDashboard /></LayoutTransition>} />
          <Route path="/admin/add-shipment" element={<LayoutTransition><AddShipment /></LayoutTransition>} />
          <Route path="/admin/edit-shipment/:trackingNumber" element={<LayoutTransition><EditShipment /></LayoutTransition>} />
          <Route path="/admin/status/:trackingNumber" element={<LayoutTransition><StatusUpdates /></LayoutTransition>} />
          <Route path="*" element={<LayoutTransition><NotFound /></LayoutTransition>} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AnimatedRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
